import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, Column, EmptyState, PageHeader, Pagination, SegmentedControl, Select, Table, TextField } from "stories/Admin";
import useFetch from "hooks/useFetch";
import styles from "./HijriPage.module.scss";

interface CalendarDate { id: number; gregorianDate: string; hijriMonth: string; hijriDate: string; specialDateName: string | null; }
interface CalendarYear { dates: CalendarDate[]; missingDates: string[]; }
interface Generation { created: number; missingDates: string[]; }
const formatDate = (value: string) => value.replace(/^(\d{4})-(\d{2})-(\d{2})$/, "$3.$2.$1");
const currentYear = new Date().getFullYear();
const years = Array.from({ length: 12 }, (_, i) => currentYear - 2 + i);

const HijriPage: React.FC = () => {
  const api = useFetch<CalendarYear | Generation | { saved: number }>();
  const [year, setYear] = useState(currentYear);
  const [calendar, setCalendar] = useState<CalendarYear>({ dates: [], missingDates: [] });
  const [view, setView] = useState<"year" | "special">("year");
  const [source, setSource] = useState("NEW");
  const [month, setMonth] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [revision, setRevision] = useState(0);
  useEffect(() => {
    let active = true;
    setCalendar({ dates: [], missingDates: [] });
    api.execute(`/api/v1/admin/calendar/${year}`, "GET").then(result => {
      if (!active) return;
      if (result && "dates" in result) setCalendar(result);
      else setError(api.lastError()?.message || "Kunne ikke hente årskalenderen.");
    });
    return () => { active = false; };
    // execute is recreated by useFetch; year and explicit refresh control loading.
  }, [year, revision]);
  // Bare nytt år tømmer feilen: oppdateringen etter en mislykket synk skal ikke skjule hvorfor den feilet.
  useEffect(() => { setError(""); }, [year]);
  useEffect(() => { setPage(1); }, [year, month, search, view]);
  const months = Array.from(new Set(calendar.dates.map(d => d.hijriMonth)));
  const rows = calendar.dates.filter(d => (view === "year" || !!d.specialDateName?.trim()) && (!month || month === d.hijriMonth) && `${d.specialDateName || ""} ${d.hijriDate} ${d.gregorianDate}`.toLowerCase().includes(search.toLowerCase()));
  const generate = async () => {
    const result = await api.execute(`/api/v1/admin/calendar/${year}/generate`, "POST");
    if (!result || !("created" in result)) throw new Error(api.lastError()?.message || "Kunne ikke fylle kalenderen fra månedstartene.");
    setMessage(`${result.created} dager lagt til. ${result.missingDates.length === 0 ? "Hele året er klart for Mawaqeet." : `${result.missingDates.length} dager mangler månedstarter. Første manglende dato: ${formatDate(result.missingDates[0])}.`}`);
  };
  const run = async (sync: boolean) => {
    setBusy(true); setError(""); setMessage("");
    try {
      if (sync) {
        const requests = [{ y: year - 1, m: 12 }, ...Array.from({ length: 12 }, (_, i) => ({ y: year, m: i + 1 })), { y: year + 1, m: 1 }];
        for (const item of requests) {
          setMessage(`Henter Hijri-datoer fra ${source === "NEW" ? "nytt" : "gammelt"} API: ${item.m}/${item.y} …`);
          const result = await api.execute(`/api/v1/admin/calendar/bonnetid/${item.y}/${item.m}/dates`, "POST", undefined, { source });
          if (!result) throw new Error(api.lastError()?.message || `Kunne ikke hente ${item.m}/${item.y}. Tidligere synkroniserte måneder er lagret.`);
        }
      }
      await generate();
    } catch (e) { setError(e instanceof Error ? e.message : "Synkroniseringen feilet."); }
    finally { setBusy(false); setRevision(v => v + 1); }
  };
  const columns: Column<CalendarDate>[] = [
    { key: "greg", header: "Gregoriansk dato", render: d => formatDate(d.gregorianDate) },
    { key: "hijri", header: "Hijri-dato", render: d => { const [y, , day] = d.hijriDate.split("-"); return `${Number(day)}. ${d.hijriMonth} ${y}`; } },
    { key: "month", header: "Hijri-måned", render: d => <Badge tone="gold">{d.hijriMonth}</Badge> },
    { key: "name", header: "Merkedag", render: d => d.specialDateName || "—" },
  ];
  return <>
    <PageHeader title="Hijri-kalender" subtitle="Full årsoversikt og merkedager. Kalenderen brukes også ved generering av Mawaqeet-tider." />
    <Card title="Synkroniser og fyll ut kalenderen">
      <p>Hent datoer fra valgt Bønnetid-kilde, eller fyll dagene mellom registrerte månedstarter. Neste månedstart bestemmer om måneden har 29 eller 30 dager. Registrerte merkedager beholdes.</p>
      <div className={styles.filters}>
        <Select label="API-kilde" value={source} onChange={e => setSource(e.target.value)} disabled={busy}><option value="NEW">Nytt Bønnetid-API</option><option value="LEGACY">Gammelt Bønnetid-API</option></Select>
        <Select label="År" value={year} onChange={e => { setYear(Number(e.target.value)); setMonth(""); }} disabled={busy}>{years.map(y => <option key={y} value={y}>{y}</option>)}</Select>
        <Button onClick={() => run(true)} disabled={busy}>Synkroniser Hijri-år</Button>
        <Button variant="secondary" onClick={() => run(false)} disabled={busy}>Fyll fra månedstarter</Button>
      </div>
      {message && <Alert tone="info">{message}</Alert>}
      {error && <Alert tone="error">{error}</Alert>}
      {!busy && calendar.missingDates.length > 0 && <Alert tone="warn">{calendar.dates.length} datoer registrert. {calendar.missingDates.length} dager mangler. Første: {formatDate(calendar.missingDates[0])}. Synkroniser eller legg inn de manglende månedstartene før Mawaqeet-generering.</Alert>}
    </Card>
    <div className={styles.filters}>
      <SegmentedControl label="Visning" value={view} onChange={setView} options={[{ value: "year", label: "Hele året" }, { value: "special", label: "Merkedager" }]} />
      <TextField aria-label="Søk i kalender" placeholder="Søk etter navn eller dato …" value={search} onChange={e => setSearch(e.target.value)} />
      <Select aria-label="Hijri-måned" value={month} onChange={e => setMonth(e.target.value)}><option value="">Alle Hijri-måneder</option>{months.map(m => <option key={m}>{m}</option>)}</Select>
    </div>
    <Card className={styles.tableCard}><Table columns={columns} rows={rows.slice((page - 1) * 31, page * 31)} rowKey={d => d.id} empty={<EmptyState icon="hijri" title="Ingen datoer">Synkroniser kalenderen for {year}, eller endre filter.</EmptyState>} /><Pagination page={page} pageCount={Math.ceil(rows.length / 31)} onChange={setPage} /></Card>
  </>;
};
export default HijriPage;
